import { api } from '@/lib/api'
import type {
  Class,
  ClassCreateInput,
  ClassGoldExample,
  ClassGoldExampleCreateInput,
  ClassSignal,
  ClassSignalsBulkInput,
  ClassUpdateInput,
  ClassWithDetails,
  RegexRule,
  RegexRuleCreateInput,
  RegexRuleUpdateInput,
  SignalConflictReport,
} from '@sophia/shared-schemas'

export interface ClassListItem extends Class {
  signalCount: number
  goldExampleCount: number
  ruleCount: number
}

export const classesApi = {
  list: async (): Promise<ClassListItem[]> => {
    const { data } = await api.get<ClassListItem[]>('/classes')
    return data
  },

  detail: async (id: string): Promise<ClassWithDetails> => {
    const { data } = await api.get<ClassWithDetails>(`/classes/${id}`)
    return data
  },

  create: async (input: ClassCreateInput): Promise<Class> => {
    const { data } = await api.post<Class>('/classes', input)
    return data
  },

  update: async (id: string, input: ClassUpdateInput): Promise<Class> => {
    const { data } = await api.patch<Class>(`/classes/${id}`, input)
    return data
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/classes/${id}`)
  },

  replaceSignals: async (id: string, input: ClassSignalsBulkInput): Promise<ClassSignal[]> => {
    const { data } = await api.put<ClassSignal[]>(`/classes/${id}/signals`, input)
    return data
  },

  checkSignalConflicts: async (id: string, input: ClassSignalsBulkInput): Promise<SignalConflictReport> => {
    const { data } = await api.post<SignalConflictReport>(`/classes/${id}/check-signal-conflicts`, input)
    return data
  },

  addExample: async (id: string, input: ClassGoldExampleCreateInput): Promise<ClassGoldExample> => {
    const { data } = await api.post<ClassGoldExample>(`/classes/${id}/examples`, input)
    return data
  },

  removeExample: async (id: string, exampleId: string): Promise<void> => {
    await api.delete(`/classes/${id}/examples/${exampleId}`)
  },

  generate: async (id: string): Promise<ClassWithDetails> => {
    const { data } = await api.post<ClassWithDetails>(`/classes/${id}/generate-regex`)
    return data
  },

  createRule: async (id: string, input: RegexRuleCreateInput): Promise<RegexRule> => {
    const { data } = await api.post<RegexRule>(`/classes/${id}/rules`, input)
    return data
  },

  updateRule: async (id: string, ruleId: string, input: RegexRuleUpdateInput): Promise<RegexRule> => {
    const { data } = await api.patch<RegexRule>(`/classes/${id}/rules/${ruleId}`, input)
    return data
  },

  removeRule: async (id: string, ruleId: string): Promise<void> => {
    await api.delete(`/classes/${id}/rules/${ruleId}`)
  },
}

export const classesQueryKey = {
  all: ['classes'] as const,
  list: () => [...classesQueryKey.all, 'list'] as const,
  detail: (id: string) => [...classesQueryKey.all, 'detail', id] as const,
}
